"use client";

import { useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { EmojiSelect, Select } from "@/components/ui";
import { EmojiIcon } from "@/components/ui/emoji/EmojiIcon";
import { useIsWindows } from "@/hooks";
import {
  getPublisherGameTypeSelectOptions,
  type PublisherGameType,
} from "../config/gameTypes";
import {
  getTopSellersRegionIconOptions,
  getTopSellersRegionSelectOptions,
} from "@/features/topsellers/config/regions";
import { createSearchParamsUpdater } from "../utils/updateSearchParams";

interface PublisherTopSellersFiltersProps {
  type: PublisherGameType;
  cc: string;
}

export function PublisherTopSellersFilters({
  type,
  cc,
}: PublisherTopSellersFiltersProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const isWindows = useIsWindows();
  const updateParams = createSearchParamsUpdater(searchParams, pathname);

  const handleTypeChange = (nextType: string) => {
    const url = updateParams.update(
      "type",
      nextType === "all" ? null : nextType
    );
    startTransition(() => {
      router.push(url);
    });
  };

  const handleRegionChange = (nextCc: string) => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    if (nextCc === "global") {
      params.delete("cc");
    } else {
      params.set("cc", nextCc);
    }
    params.set("page", "1");
    startTransition(() => {
      router.push(`${pathname}?${params.toString()}`);
    });
  };

  return (
    <div className="inline-flex items-center rounded-md border border-[#2a475e] bg-[#16202d] shadow-sm overflow-visible h-10 transition-all hover:border-[#3a556e]">
      {/* Type Select */}
      <div className="flex items-center gap-3 px-3 sm:px-4 py-2">
        <span className="hidden sm:inline text-xs sm:text-sm text-[#8f98a0] whitespace-nowrap leading-none">
          Type
        </span>
        <div className="flex-1 min-w-0 relative group">
          <div className="absolute inset-0 rounded -mx-1 bg-[#1f2e3f]/0 group-hover:bg-[#1f2e3f]/50 transition-colors pointer-events-none" />
          {isWindows ? (
            <EmojiSelect
              value={type}
              onChange={handleTypeChange}
              options={getPublisherGameTypeSelectOptions()}
              isLoading={isPending}
              className="relative z-10 !h-full text-xs min-w-[140px] sm:text-sm sm:min-w-[180px] !border-0 !bg-transparent !rounded-none !py-0 focus:!ring-0 cursor-pointer leading-none"
              ariaLabel="Game Type"
            />
          ) : (
            <Select
              value={type}
              onChange={handleTypeChange}
              options={getPublisherGameTypeSelectOptions()}
              isLoading={isPending}
              className="relative z-10 h-full text-xs min-w-[140px] sm:text-sm sm:min-w-[180px] !border-0 !bg-transparent !rounded-none !py-0 focus:!ring-0 cursor-pointer leading-none"
              ariaLabel="Game Type"
            />
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="h-5 sm:h-6 w-px bg-[#2a475e]" aria-hidden="true" />

      {/* Region Select */}
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2">
        <span className="hidden sm:inline text-xs sm:text-sm text-[#8f98a0] whitespace-nowrap leading-none">
          Region
        </span>
        <div className="flex-1 min-w-0 relative group">
          <div className="absolute inset-0 rounded -mx-1 bg-[#1f2e3f]/0 group-hover:bg-[#1f2e3f]/50 transition-colors pointer-events-none" />
          {isWindows ? (
            <EmojiSelect
              value={cc}
              onChange={handleRegionChange}
              options={getTopSellersRegionIconOptions().map((o) => ({
                value: o.value,
                label: o.label,
                icon: <EmojiIcon emoji={o.emoji} size={18} title={o.label} />,
              }))}
              isLoading={isPending}
              className="relative z-10 !h-full text-xs min-w-[160px] sm:text-sm sm:min-w-[220px] !border-0 !bg-transparent !rounded-none !py-0 focus:!ring-0 cursor-pointer leading-none"
              ariaLabel="Region"
            />
          ) : (
            <Select
              value={cc}
              onChange={handleRegionChange}
              options={getTopSellersRegionSelectOptions()}
              isLoading={isPending}
              className="relative z-10 h-full text-xs min-w-[160px] sm:text-sm sm:min-w-[220px] !border-0 !bg-transparent !rounded-none !py-0 focus:!ring-0 cursor-pointer leading-none"
              ariaLabel="Region"
            />
          )}
        </div>
      </div>
    </div>
  );
}
